import React, { useState } from "react";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import { api, formatApiError } from "@/lib/api";
import { PAGES } from "@/constants/testIds";
import { toast } from "sonner";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name, email, message });
      toast.success("Message received. We'll be in touch soon.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  }

  return (
    <div data-testid={PAGES.contact} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="Contact"
        title="Write to the"
        italic="curators"
        tail="of the archive."
        subtitle="Have a family story to submit, a historic record to share, or an idea for the platform? We read every note — usually within a couple of days."
      />

      <section className="relative py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <div className="border border-ivory-300 rounded-2xl p-8 bg-ivory">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Editorial</div>
              <div className="mt-2 font-serif text-2xl text-ink">stories@indianfamilytree.com</div>
              <p className="mt-2 text-sm text-ink-50">Family stories, oral histories, village archives</p>
            </div>
            <div className="border border-ivory-300 rounded-2xl p-8 bg-ivory mt-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Partnerships</div>
              <div className="mt-2 font-serif text-2xl text-ink">network@socialpulse.co</div>
              <p className="mt-2 text-sm text-ink-50">Institutions, libraries, archives, foundations</p>
            </div>
            <div className="border border-ivory-300 rounded-2xl p-8 bg-ivory mt-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Studio</div>
              <div className="mt-2 font-serif text-2xl text-ink">A SocialPulse project · India</div>
              <p className="mt-2 text-sm text-ink-50">Operating hours: Monday to Friday</p>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <form onSubmit={submit} data-testid={PAGES.contactForm} className="border border-ivory-300 rounded-2xl p-8 md:p-10 bg-ivory-100/60">
              <h3 className="font-serif text-3xl tracking-tight text-ink">Send a note</h3>
              <div className="mt-8 space-y-6">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Your name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={PAGES.contactName}
                    required
                    className="mt-2 w-full h-12 bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none font-serif text-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={PAGES.contactEmail}
                    required
                    className="mt-2 w-full h-12 bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none font-serif text-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid={PAGES.contactMessage}
                    required
                    rows={5}
                    className="mt-2 w-full bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none font-serif text-lg py-2 resize-none"
                    placeholder="Tell us about your family, or how we can help."
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid={PAGES.contactSubmit}
                  className="rounded-full bg-saffron text-ivory px-7 py-3.5 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
                >
                  {busy ? "Sending…" : "Send message →"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
